import type { Axis } from 'highcharts/highcharts.src';
import type Highcharts from 'highcharts/highstock';
import moment from 'moment';
import { useRef } from 'react';
import { DEFAULT_CHART_NAVIGATOR_RANGE } from '../../config/constants';
import type { IXaxisExtremes } from '../../interfaces/projection';

interface IProps {
    updateEventCoordinates: (chart: Highcharts.Chart) => void;
    setChart?: (chart: Highcharts.Chart) => void;
}

export const useChartNavigatorCallbacks = ({ updateEventCoordinates, setChart }: IProps) => {
    const xAxisExtremes = useRef<IXaxisExtremes | undefined>(undefined);

    /**
     * Calculates the minimum and maximum epoch unix timestamps for the chart navigator based on the current projection period.
     * When the chart renders the first time, default values are set.
     */
    const setMinMaxNavigatorTimeStamps = (chart: Highcharts.Chart) => {
        const xAxis = chart.xAxis[0] as Axis;
        const projectionMin = xAxis.min;
        const projectionMax = xAxis.max;
        const navigatorExtremes = xAxisExtremes.current;
        let minMaxExtremes: IXaxisExtremes;

        if (rendersFirstTime(navigatorExtremes)) {
            minMaxExtremes = getDefaultNavigatorTimeRange();
        } else {
            minMaxExtremes = getNavigatorMinMaxTimeStamps(projectionMin!, projectionMax!, navigatorExtremes);
        }
        xAxis.setExtremes(minMaxExtremes.min, minMaxExtremes.max, true, false);
    };

    const setChartInstance = (chart: Highcharts.Chart) => {
        setChart?.(chart);
    };

    const setMaxWidthOfNavigator = (chart: Highcharts.Chart) => {
        chart.update({ navigator: { xAxis: { width: chart.chartWidth - 10 } } });
    };

    const rendersFirstTime = (navigatorExtremes?: IXaxisExtremes) => !navigatorExtremes;

    const getDefaultNavigatorTimeRange = () => {
        const min = moment().valueOf();
        const max = min + DEFAULT_CHART_NAVIGATOR_RANGE;

        return { min, max };
    };

    const getNavigatorMinMaxTimeStamps = (projectionMin: number, projectionMax: number, navigatorExtremes: IXaxisExtremes): IXaxisExtremes => {
        const max = getMaxNavigatorTimeStamp(projectionMax, navigatorExtremes);
        const min = getMinNavigatorTimeStamp(projectionMin, navigatorExtremes);

        return { max, min };
    };

    const getMinNavigatorTimeStamp = (projectionMin: number, navigatorExtremes: IXaxisExtremes) => {
        if (navigatorExtremes.min < projectionMin) {
            return projectionMin;
        } else {
            return navigatorExtremes.min;
        }
    };

    const getMaxNavigatorTimeStamp = (projectionMax: number, navigatorExtremes: IXaxisExtremes) => {
        if (navigatorExtremes.max > projectionMax) {
            return projectionMax;
        } else {
            return navigatorExtremes.max;
        }
    };

    const setExtremes = (chart: Highcharts.Chart) => {
        const { min, max } = chart.xAxis[0];
        // @ts-expect-error  Problems with missing Highcharts types
        xAxisExtremes.current = { min, max };
        updateEventCoordinates(chart);
    };

    return {
        setExtremes,
        setMinMaxNavigatorTimeStamps,
        setMaxWidthOfNavigator,
        setChartInstance,
    };
};
